import type { PaymentOptionType } from "@/lib/api/types";
import {
  WALLET_PROVIDERS,
  type WalletProviderValue,
} from "@/modules/onboarding/constants/wallet-providers";

// The backend stores payment options generically as a `type` + a list of
// {labelAr, labelEn, value} entries. The guided editor reconstructs a typed
// shape from those entries (by matching the canonical English labels the
// onboarding/Gemini flow also used) and serialises back to the same entries.

export type PaymentEntry = { labelAr: string; labelEn: string; value: string };

export type InstaPayReceiver = "PHONE" | "IPA" | "CARD" | "BANK_ACCOUNT";

export type GuidedPaymentData =
  | { kind: "wallet"; provider: WalletProviderValue | ""; phone: string }
  | {
      kind: "instapay";
      receiver: InstaPayReceiver;
      phone: string;
      ipa: string;
      cardNumber: string;
      cardHolder: string;
      bankName: string;
      holder: string;
      account: string;
    }
  | {
      kind: "bank";
      bankName: string;
      holder: string;
      account: string;
      iban: string;
    }
  | { kind: "other"; entries: PaymentEntry[] };

export type GuidedKind = GuidedPaymentData["kind"];

// Canonical bilingual labels — these become the stored entry labels and the
// keys the parser matches on. Keep in sync with onboarding's persisted shape.
const LBL = {
  provider: { ar: "المزود", en: "Provider" },
  walletNumber: { ar: "رقم المحفظة", en: "Wallet Number" },
  phone: { ar: "رقم الهاتف", en: "Phone Number" },
  ipa: { ar: "عنوان IPA", en: "IPA Handle" },
  cardNumber: { ar: "رقم البطاقة", en: "Card Number" },
  cardHolder: { ar: "اسم حامل البطاقة", en: "Card Holder" },
  bankName: { ar: "اسم البنك", en: "Bank Name" },
  holder: { ar: "اسم المستلم", en: "Receiver Name" },
  account: { ar: "رقم الحساب", en: "Account Number" },
  iban: { ar: "آيبان (IBAN)", en: "IBAN" },
} as const;

// Legacy label some options were stored with (onboarding/Gemini combined
// account number and IBAN into one field). Still matched on read.
const LEGACY_ACCOUNT_EN = "Account / IBAN";

const entry = (l: { ar: string; en: string }, value: string): PaymentEntry => ({
  labelAr: l.ar,
  labelEn: l.en,
  value,
});

const KIND_TO_TYPE: Record<
  Exclude<GuidedKind, "other">,
  PaymentOptionType
> = {
  wallet: "WALLET",
  instapay: "INSTAPAY",
  bank: "BANK",
};

export function guidedKindToType(kind: GuidedKind): PaymentOptionType {
  return kind === "other" ? "OTHER" : KIND_TO_TYPE[kind];
}

function providerLabel(provider: WalletProviderValue | ""): string {
  return WALLET_PROVIDERS.find((p) => p.value === provider)?.labelEn ?? "";
}

/** Serialise the guided shape into the generic entries the backend stores. */
export function guidedToEntries(data: GuidedPaymentData): PaymentEntry[] {
  switch (data.kind) {
    case "wallet":
      return [
        entry(LBL.provider, providerLabel(data.provider)),
        entry(LBL.walletNumber, data.phone.trim()),
      ];
    case "instapay":
      if (data.receiver === "PHONE") return [entry(LBL.phone, data.phone.trim())];
      if (data.receiver === "IPA") return [entry(LBL.ipa, data.ipa.trim())];
      if (data.receiver === "CARD")
        return [
          entry(LBL.cardHolder, data.cardHolder.trim()),
          entry(LBL.cardNumber, data.cardNumber.trim()),
        ];
      return [
        entry(LBL.bankName, data.bankName.trim()),
        entry(LBL.holder, data.holder.trim()),
        entry(LBL.account, data.account.trim()),
      ];
    case "bank":
      // Two shapes: (receiver + bank name + account number) OR (receiver + IBAN).
      if (data.iban.trim()) {
        return [
          entry(LBL.holder, data.holder.trim()),
          entry(LBL.iban, data.iban.trim()),
        ];
      }
      return [
        entry(LBL.holder, data.holder.trim()),
        entry(LBL.bankName, data.bankName.trim()),
        entry(LBL.account, data.account.trim()),
      ];
    case "other":
      return data.entries
        .map((e) => ({
          labelAr: e.labelAr.trim(),
          labelEn: e.labelEn.trim(),
          value: e.value.trim(),
        }))
        .filter((e) => e.labelAr || e.labelEn || e.value);
  }
}

function findValue(entries: PaymentEntry[], en: string): string | undefined {
  const hit = entries.find(
    (e) => e.labelEn.trim().toLowerCase() === en.toLowerCase(),
  );
  return hit?.value;
}

function findHolder(entries: PaymentEntry[]): string {
  return findValue(entries, LBL.holder.en) ?? findValue(entries, "Account Holder") ?? "";
}

function findAccount(entries: PaymentEntry[]): string | undefined {
  return findValue(entries, LBL.account.en) ?? findValue(entries, LEGACY_ACCOUNT_EN);
}

function matchProvider(value: string | undefined): WalletProviderValue | "" {
  if (!value) return "";
  const hit = WALLET_PROVIDERS.find(
    (p) => p.labelEn === value || p.labelAr === value || p.value === value,
  );
  return hit?.value ?? "";
}

/**
 * Reconstruct the guided shape from a stored option. Returns an "other" shape
 * (raw entries) when the entries don't match a known guided layout, so legacy
 * / hand-built options still edit cleanly via the generic fallback.
 */
export function entriesToGuided(
  type: PaymentOptionType,
  entries: PaymentEntry[],
): GuidedPaymentData {
  const fallback: GuidedPaymentData = { kind: "other", entries };

  if (type === "WALLET") {
    const phone = findValue(entries, LBL.walletNumber.en);
    if (phone === undefined) return fallback;
    return {
      kind: "wallet",
      provider: matchProvider(findValue(entries, LBL.provider.en)),
      phone,
    };
  }

  if (type === "INSTAPAY") {
    const base = {
      kind: "instapay" as const,
      phone: "",
      ipa: "",
      cardNumber: "",
      cardHolder: "",
      bankName: "",
      holder: "",
      account: "",
    };
    const ipa = findValue(entries, LBL.ipa.en);
    if (ipa !== undefined) {
      return { ...base, receiver: "IPA", ipa };
    }
    const cardNumber = findValue(entries, LBL.cardNumber.en);
    if (cardNumber !== undefined) {
      return {
        ...base,
        receiver: "CARD",
        cardNumber,
        cardHolder: findValue(entries, LBL.cardHolder.en) ?? "",
      };
    }
    const bankName = findValue(entries, LBL.bankName.en);
    if (bankName !== undefined) {
      return {
        ...base,
        receiver: "BANK_ACCOUNT",
        bankName,
        holder: findHolder(entries),
        account: findAccount(entries) ?? "",
      };
    }
    const phone = findValue(entries, LBL.phone.en);
    if (phone !== undefined) {
      return { ...base, receiver: "PHONE", phone };
    }
    return fallback;
  }

  if (type === "BANK") {
    const bankName = findValue(entries, LBL.bankName.en);
    const iban = findValue(entries, LBL.iban.en);
    const account = findAccount(entries);
    if (bankName === undefined && iban === undefined && account === undefined)
      return fallback;
    return {
      kind: "bank",
      bankName: bankName ?? "",
      holder: findHolder(entries),
      account: account ?? "",
      iban: iban ?? "",
    };
  }

  return fallback;
}
